import { isNumber } from 'lodash'

import { CatalogAttribute, Filter } from '@marketplace-web/catalog/catalog-data'
import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { Screen } from 'constants/tracking/screens'

type ClickEventArgs = {
  target: ClickableElement
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: ClickableElement
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

export const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type FollowUserEventArgs = {
  userId: number
  isFollowEvent: boolean
  contentSource?: ContentSource
}

type FollowUserEventExtra = {
  user_id: number
  content_source?: ContentSource
}

export const followUserEvent = ({
  userId,
  isFollowEvent,
  contentSource,
}: FollowUserEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_user' : 'user.unfollow_user'

  const extra: FollowUserEventExtra = {
    user_id: userId,
  }

  if (isFollowEvent) extra.content_source = contentSource

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

export type ClickListItemEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchScore?: string
  searchSignals?: Array<string> | null
  itemDistance?: number | null
  homepageSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ClickListEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_score?: string
  search_signals?: Array<string>
  buyer_item_distance_km?: number
  homepage_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

export const clickListItemEvent = (args: ClickListItemEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId,
    screen,
    metadata,
  } = args

  const extra: ClickListEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (searchScore) extra.search_score = searchScore
  if (searchSignals) extra.search_signals = searchSignals
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'user.click_list_item',
    extra,
  }
}

type ProfileClickEventArgs = {
  target: ClickableElement
  ownerId: string
  screen?: Screen
  itemId?: string
}

type ProfileClickEventExtra = {
  target: ClickableElement
  owner_id: string
  screen?: Screen
  item_id?: string
}

export const profileClickEvent = (args: ProfileClickEventArgs): TrackingEvent => {
  const { target, ownerId, screen, itemId } = args

  const extra: ProfileClickEventExtra = { target, owner_id: ownerId }

  if (screen) extra.screen = screen
  if (itemId) extra.item_id = itemId

  return {
    event: 'profile.click',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget =
  | 'facebook_like_button'
  | 'my_items_grid'
  | 'my_items_list'
  | 'my_favorites_grid'
  | 'my_favorites_list'
  | 'catalog_grid'
  | 'catalog_list'
  | 'nps_survey'
  | 'sell_tab_survey'
  | 'seller_after_upload_instructions'
  | 'seller_after_sale_instructions'
  | 'seller_after_transaction_complete_instructions'
  | 'email_confirmation'
  | 'email_confirmed'
  | 'video'
  | 'seller_banner'
  | 'sell_tab_banner'
  | 'seller_funnel_welcome_slide'
  | 'wallet_setup_success'
  | 'wallet_pay_in_success'
  | 'newsletter_subscription_banner'
  | 'editorial_campaign_banner'
  | 'cta_prompt'
  | 'feed_personalization_banner'
  | 'personalization_banner_after_feed'
  | 'ad_box'
  | 'bump_promotion'
  | 'onboarding_modal_card'
  | 'app_rating_dialog'
  | 'upload_onboarding_carousel_card'
  | 'notification'
  | 'see_whole_closet_cta'
  | 'cookie_and_ad_personalization_banner'
  | 'ad_personalization_toggle'
  | 'shipping_price'
  | 'translation_hint'
  | 'listing_increase_banner'
  | 'taxpayers_report'
  | 'taxpayers_tax_rules'
  | 'taxpayers_dac7_banner'
  | 'taxpayers_balance_block_modal'
  | 'taxpayers_sales_block_modal'
  | 'taxpayers_tax_rules_testimonial'
  | 'local_search_suggestion'
  | 'brand_cluster_section'
  | 'photo_picker_info_banner'
  | 'crm_message_video'
  | 'free_return_label_modal'
  | 'catalog_popular_catalog'
  | 'label_type_selection'
  | 'return_order'
  | 'ship_fast_badge_description'
  | 'video_story_feed'
  | 'video_story'
  | 'video_story_upload'
  | 'verify'
  | 'didnt_receive_email'
  | 'start'
  | 'free_bump_top_card'
  | 'free_bump_bottom_banner'
  | 'free_bump_success_message'
  | 'physical_auth_buyer'
  | 'physical_auth_seller'
  | 'favorite_button'
  | 'shipping_discount_details'
  | 'physical_auth_eligible'
  | 'recommended_items_grid'
  | 'shop_bundle'
  | 'measurements_info'
  | 'value_select_modal'
  | 'crm_in_app_message_carousel_slide'
  | 'onboarding_video'
  | 'drop_off_point'
  | 'pricing_info_note'
  | 'taxpayers_dac7_special_verification_banner'
  | 'see_drop_off_points'
  | 'message_action'
  | 'bundle_discount_settings'
  | 'taxpayers_special_verification_balance_block_modal'
  | 'sca_start_modal'
  | 'extend_shipping_deadline'
  | 'verification_completed'
  | 'cp_reminder'
  | 'select_parcel_size'
  | 'user_last_seen'
  | 'seller_feedbacks'
  | 'feedback'
  | 'social_proof_banner'
  | 'promotional_listing_banner'
  | 'electronics_verification_buyer'
  | 'electronics_verification_eligible'
  | 'electronics_verification_seller'
  | 'view_all_user_items_card_cta'
  | 'more_similar_items_card_cta'
  | 'photo_tips'
  | 'username_change_disabled'
  | 'carrier_disable_feedback'
  | 'bump_precheckout_review_order_action'
  | 'get_instant_mobile_notifications_banner'
  | 'seller_insights_blocks'
  | 'campaign_landing_page'
  | 'homepage_icon_modal'
  | 'mv_visibility_banner'
  | 'pending_balance'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type ClickFilterEventArgs = {
  filter: Filter
  screen?: Screen
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ClickFilterEventExtra = {
  filter: Filter
  screen?: Screen
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

export const clickFilterEvent = (args: ClickFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ClickFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.click_filter',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: Screen
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

export const userInputEvent = (args: UserInputEventArgs): TrackingEvent => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}

type SelectCatalogEventArgs = {
  catalogIds: Array<number>
  attributeId?: CatalogAttribute
  screen?: Screen
}

type SelectCatalogEventExtra = {
  catalog_id: string
  attribute_id: number | null
  screen?: string
}

export const selectCatalogEvent = (args: SelectCatalogEventArgs): TrackingEvent => {
  const { catalogIds, attributeId, screen } = args

  const extra: SelectCatalogEventExtra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId ?? null,
  }

  if (screen) extra.screen = screen

  return {
    event: 'user.select_catalog',
    extra,
  }
}

type SettingsUserInputEventArgs = {
  screen: string
  target: string
  isValid: boolean
  targetDetails?: string
}

type SettingsUserInputEventExtra = {
  screen: string
  target: string
  valid: boolean
  target_details?: string
}

export const settingsUserInputEvent = (args: SettingsUserInputEventArgs): TrackingEvent => {
  const { screen, target, isValid, targetDetails } = args

  const extra: SettingsUserInputEventExtra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails,
  }

  return {
    event: 'settings.user.input',
    extra,
  }
}

type DonationsClickEventArgs = {
  target: ClickableElement
  screen?: Screen | null
  targetDetails?: string | number
}

type DonationsClickEventExtra = {
  target: string
  screen?: string
  target_details?: string | number
}

export const donationsClickEvent = (args: DonationsClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails } = args

  const extra: DonationsClickEventExtra = {
    target,
  }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'user.click_donations',
    extra,
  }
}

type ItemUploadClickEventArgs = {
  screen?: Screen
  target: ClickableElement
  targetDetails?: string | null
  uploadSessionId: string | null
}

type ItemUploadClickEventExtra = {
  screen?: Screen
  target: ClickableElement
  target_details?: string | null
  upload_session_id: string | null
}

export const itemUploadClickEvent = (args: ItemUploadClickEventArgs): TrackingEvent => {
  const { screen, target, targetDetails, uploadSessionId } = args

  const extra: ItemUploadClickEventExtra = {
    target,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'upload_form.user.click',
    extra,
  }
}

type ItemUploadViewScreenEventArgs = {
  screen: Screen
  details?: string
  uploadSessionId: string | null
}

type ItemUploadViewScreenEventExtra = {
  screen: Screen
  details?: string
  upload_session_id: string | null
}

export const itemUploadViewScreenEvent = (args: ItemUploadViewScreenEventArgs): TrackingEvent => {
  const { screen, details, uploadSessionId } = args

  const extra: ItemUploadViewScreenEventExtra = {
    screen,
    upload_session_id: uploadSessionId,
  }

  if (details) extra.details = details

  return {
    event: 'upload_form.user.view_screen',
    extra,
  }
}

type InboxSupportFeedbackEventsBaseArgs = {
  threadId: string
  helpCenterSessionId: string
}

type InboxSupportFeedbackClosedEventsArgs = InboxSupportFeedbackEventsBaseArgs & {
  rating?: number
}

export const chatbotFeedbackClosed = (args: InboxSupportFeedbackClosedEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    rating: args.rating,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_closed',
    extra,
  }
}

export const autoreplyFeedbackClosed = (args: InboxSupportFeedbackClosedEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    rating: args.rating,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_closed',
    extra,
  }
}

export const chatbotFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_v1_viewed',
    extra,
  }
}

export const autoreplyFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_v1_viewed',
    extra,
  }
}

type ReferralsOpenLinkEventArgs = {
  referrerId: number
}

type ReferralsOpenLinkEventExtra = {
  referrer_id: number
}

export const referralsOpenLinkEvent = (args: ReferralsOpenLinkEventArgs): TrackingEvent => {
  const { referrerId } = args

  const extra: ReferralsOpenLinkEventExtra = {
    referrer_id: referrerId,
  }

  return {
    event: 'sharing.referrals.open_link',
    extra,
  }
}

type SystemTimingArgs = {
  section: 'largest_contentful_paint' | 'ad_load' | 'van_ad_load' | 'van_ad_asset_load'
  duration: number
  completionState: 'succeeded'
  data?: string
}

type SystemTimingExtra = {
  section: SystemTimingArgs['section']
  /** Duration in millisecond */
  duration: number
  completion_state: 'succeeded'
  data?: string
}

export const systemTimingEvent = (args: SystemTimingArgs) => {
  const { section, duration, completionState, data } = args

  const extra: SystemTimingExtra = { section, duration, completion_state: completionState, data }

  return {
    event: 'system.timing',
    extra,
  }
}

type ShippingClickEventArgs = {
  screen: string
  checkoutId: string
  target: ClickableElement
  valid?: boolean
  info?: string
  infoPii?: string
}

type ShippingClickEventExtra = {
  screen: string
  checkout_id: string
  target: string
  valid?: boolean
  info?: string
  info_pii?: string
}

export const shippingClickEvent = (args: ShippingClickEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, target, info, infoPii } = args

  const extra: ShippingClickEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target,
    info,
    info_pii: infoPii,
  }

  return { event: 'shipping_options.click', extra }
}
